import React, { useEffect, useMemo, useState } from 'react'

import { DateTime } from 'luxon'
import { RRule } from 'rrule'

import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'

interface ConfigurationPreviewProps {
	rrule: string | null
	exdate: string[]
	timeFrom?: string
	startDate?: Date
	endDate?: Date
}

/**
 * Parse our custom RRULE format into RRule options.
 * Our format: "FREQ=X;INTERVAL=Y;...;DTSTART=YYYY-MM-DDTHH:mm:ss;UNTIL=YYYY-MM-DDTHH:mm:ss"
 * This function extracts DTSTART/UNTIL separately and builds RRule options.
 */
const parseCustomRRule = (
	rruleString: string
): {
	options: any
	dtstart: DateTime | null
	until: DateTime | null
} => {
	const parts = rruleString.split(';')
	const params: Record<string, string> = {}

	parts.forEach((part) => {
		const [key, value] = part.split('=')
		if (key && value) {
			params[key.toUpperCase()] = value
		}
	})

	// Extract and parse DTSTART/UNTIL as local times (timezone-agnostic)
	let dtstart: DateTime | null = null
	let until: DateTime | null = null

	const parseRruleDate = (value?: string): DateTime | null => {
		if (!value) return null

		// Try standard ISO first (legacy hyphenated form), then RFC basic
		// RRULE formats like 20251114T000000Z.
		let dt = DateTime.fromISO(value, { zone: 'utc' })

		if (!dt.isValid) {
			dt = DateTime.fromFormat(value, "yyyyLLdd'T'HHmmss'Z'", {
				zone: 'utc',
			})
		}

		if (!dt.isValid) {
			dt = DateTime.fromFormat(value, "yyyyLLdd'T'HHmmss", {
				zone: 'utc',
			})
		}

		return dt.isValid ? dt : null
	}

	if (params.DTSTART) {
		dtstart = parseRruleDate(params.DTSTART)
	}

	if (params.UNTIL) {
		until = parseRruleDate(params.UNTIL)
	}

	// Build RRule options from the remaining parameters
	const options: any = {}

	if (params.FREQ) {
		const freqMap: Record<string, number> = {
			YEARLY: (RRule as any).YEARLY,
			MONTHLY: (RRule as any).MONTHLY,
			WEEKLY: (RRule as any).WEEKLY,
			DAILY: (RRule as any).DAILY,
			HOURLY: (RRule as any).HOURLY,
			MINUTELY: (RRule as any).MINUTELY,
			SECONDLY: (RRule as any).SECONDLY,
		}
		options.freq = freqMap[params.FREQ] ?? (RRule as any).DAILY
	}

	if (params.INTERVAL) {
		options.interval = parseInt(params.INTERVAL, 10) || 1
	}

	if (params.COUNT) {
		options.count = parseInt(params.COUNT, 10)
	}

	if (params.WKST) {
		const wkstMap: Record<string, any> = {
			MO: (RRule as any).MO,
			TU: (RRule as any).TU,
			WE: (RRule as any).WE,
			TH: (RRule as any).TH,
			FR: (RRule as any).FR,
			SA: (RRule as any).SA,
			SU: (RRule as any).SU,
		}
		options.wkst = wkstMap[params.WKST] ?? (RRule as any).MO
	}

	if (params.BYDAY) {
		const dayMap: Record<string, any> = {
			MO: (RRule as any).MO,
			TU: (RRule as any).TU,
			WE: (RRule as any).WE,
			TH: (RRule as any).TH,
			FR: (RRule as any).FR,
			SA: (RRule as any).SA,
			SU: (RRule as any).SU,
		}
		options.byweekday = params.BYDAY.split(',')
			.map((d) => dayMap[d.trim()])
			.filter(Boolean)
	}

	if (params.BYHOUR) {
		options.byhour = params.BYHOUR.split(',')
			.map((h) => parseInt(h.trim(), 10))
			.filter((h) => !isNaN(h))
	}

	if (params.BYMINUTE) {
		options.byminute = params.BYMINUTE.split(',')
			.map((m) => parseInt(m.trim(), 10))
			.filter((m) => !isNaN(m))
	}

	if (params.BYSECOND) {
		options.bysecond = params.BYSECOND.split(',')
			.map((s) => parseInt(s.trim(), 10))
			.filter((s) => !isNaN(s))
	}

	if (params.BYYEARDAY) {
		options.byyearday = parseInt(params.BYYEARDAY, 10)
	}

	return { options, dtstart, until }
}

export const ConfigurationPreview: React.FC<ConfigurationPreviewProps> = ({
	rrule,
	exdate,
	startDate,
	endDate,
}) => {
	const { t } = useTranslation('campaigns')
	const [upcomingExecutions, setUpcomingExecutions] = useState<DateTime[]>([])
	const [hasMore, setHasMore] = useState(false)

	// Memoize dependencies to avoid unnecessary re-renders
	const dependencies = useMemo(
		() => ({
			rrule,
			exdate,
			startDate,
			endDate,
		}),
		[rrule, exdate, startDate, endDate]
	)

	// Debounce dependencies to avoid unnecessary re-renders
	const debouncedDependencies = useDebounce(dependencies, 150)

	// Calculate upcoming executions when dependencies change
	useEffect(() => {
		const { rrule, exdate, startDate, endDate } = debouncedDependencies

		if (!rrule) {
			setUpcomingExecutions([])
			setHasMore(false)
			return
		}

		try {
			// Parse our custom RRULE format
			const {
				options,
				dtstart: rruleDtstart,
				until: rruleUntil,
			} = parseCustomRRule(rrule)

			// Ghosting behavior: determine the effective start date for preview
			const today = DateTime.now().startOf('day')
			let effectiveStart = today

			// Use startDate prop if provided, otherwise fall back to DTSTART from RRULE
			const configuredStart = startDate
				? DateTime.fromJSDate(startDate).startOf('day')
				: rruleDtstart?.startOf('day')

			if (configuredStart && configuredStart > today) {
				effectiveStart = configuredStart
			}

			// Determine effective end date
			const configuredEnd = endDate
				? DateTime.fromJSDate(endDate).endOf('day')
				: rruleUntil?.endOf('day')

			// Set dtstart and until in RRule options (as UTC Date objects for timezone-agnostic behavior)
			if (effectiveStart) {
				options.dtstart = effectiveStart.toUTC().toJSDate()
			}
			if (configuredEnd) {
				options.until = configuredEnd.toUTC().toJSDate()
			}

			// Generate occurrences
			const rule = new RRule(options)
			const now = DateTime.now()

			// Filter by exdate (day precision)
			const exdateParsed = new Set(
				exdate
					.map((d) => {
						let dt = DateTime.fromFormat(d, 'yyyyMMdd')
						if (!dt.isValid) {
							dt = DateTime.fromFormat(d, 'yyyy-MM-dd')
						}
						return dt.isValid ? dt.startOf('day').toMillis() : null
					})
					.filter((m): m is number => m !== null)
			)

			const results: DateTime[] = []
			const maxDate = now.plus({ years: 5 }).toJSDate()

			;(rule as any).between(now.toJSDate(), maxDate, true, (date: Date) => {
				if (results.length >= 100) return false

				const dt = DateTime.fromJSDate(date, { zone: 'utc' })

				// Filter future only
				if (dt <= now) return true

				// Filter by exdate
				const dayMillis = dt.startOf('day').toMillis()
				if (exdateParsed.has(dayMillis)) return true

				results.push(dt)
				return true
			})

			setUpcomingExecutions(results)
			setHasMore(results.length === 100)
		} catch (error) {
			console.error('Error generating preview:', error, {
				rrule,
				startDate,
				endDate,
			})
			setUpcomingExecutions([])
			setHasMore(false)
		}
	}, [debouncedDependencies])

	return (
		<div className="flex flex-col relative h-full">
			<div className="flex-shrink-0">
				<h2 className="text-sm font-medium mb-4">
					{t('config.preview.title')}
				</h2>
			</div>

			<div className="flex-1 overflow-y-auto pr-2 relative">
				<div className="space-y-2 pr-2">
					<div className="flex items-center justify-between text-sm font-medium pb-2">
						<span>{t('config.preview.date')}</span>
						<span>{t('config.preview.time')}</span>
					</div>

					{upcomingExecutions.length === 0 && (
						<p className="text-sm text-muted-foreground">
							{t('config.preview.noExecutions')}
						</p>
					)}

					{upcomingExecutions.map((execution, idx) => (
						<div
							key={idx}
							className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0"
						>
							<span className="font-medium">
								{execution.toFormat('EEE d, LLL yyyy')}
							</span>
							<span className="text-muted-foreground">
								{execution.toFormat('HH:mm:ss')}
							</span>
						</div>
					))}

					{hasMore && (
						<div className="flex items-center justify-center gap-2 text-sm py-4 text-muted-foreground">
							<span>···</span>
							<span>{t('config.preview.limited')}</span>
						</div>
					)}
				</div>

				{hasMore && (
					<div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-card to-transparent pointer-events-none" />
				)}
			</div>
		</div>
	)
}
